<template>
  <v-card tile elevation="0" height="100%" min-height="100%">
    <v-form ref="form" v-model="isValid">
      <v-card-text>
        <v-select
          dense
          hide-details=""
          class="pb-2"
          :loading="isDataLoading"
          :items="nmoList"
          :label="
            getTranslation('patient.creationForm.formFieldTitles.nmo', '*')
          "
          required
          item-value="id"
          item-text="name"
          outlined
          v-model="patient.nmoId"
          :rules="[
            () =>
              patient.nmoId > 0 ||
              getTranslation('patient.creationForm.errors.nmoRequired'),
          ]"
          :disabled="!isUserAdmin"
          v-if="isUserAdmin"
          @change="patient.patientId != null ? validatePatientUniqueness() : null"
        ></v-select>
        <v-select
          dense
          hide-details=""
          class="pb-2"
          :label="
            getTranslation('patient.creationForm.formFieldTitles.htc', '*')
          "
          required
          :items="filteredHTCList"
          item-text="name"
          item-value="id"
          outlined
          v-model="patient.htcId"
          :disabled="filteredHTCList.length < 1"
          :loading="isDataLoading"
          v-if="!isUserDoctor"
          :rules="[
            () =>
              !!patient.htcId ||
              getTranslation('patient.creationForm.errors.htcIdRequired')
          ]"
        ></v-select>
        <v-text-field
          dense
          :hide-details="!patientExists"
          class="pb-2"
          ref="patientId"
          :label="
            getTranslation(
              'patient.creationForm.formFieldTitles.patientId',
              '*'
            )
          "
          required
          v-model="patient.patientId"
          outlined
          :disabled="!canUpdatePatientId"
          :rules="[
            () =>
              !!patient.patientId ||
              getTranslation('patient.creationForm.errors.patientIdRequired'),
            () =>
              !patientExists ||
              getTranslation('patient.creationForm.errors.patientIdTaken'),
          ]"
          @keydown="validatePatientUniqueness()"
        ></v-text-field>
        <v-text-field
          dense
          hide-details="auto"
          class="pb-2"
          ref="wbdrId"
          :label="getTranslation('patient.creationForm.formFieldTitles.wbdrId')"
          v-model="patient.wbdrId"   
          outlined
          @keydown="onKeydownWBDRId"
          @paste.prevent
          :rules="[
                  v =>  (v || '' ).length <= 5 || getTranslation('patient.creationForm.errors.wbdrIdInvalid'),
                  v => Number(v || 1 ) > 0  || getTranslation('patient.creationForm.errors.wbdrIdInvalid'),
                  () => !wbdrIdExists || getTranslation('patient.creationForm.errors.wbdrIdTaken'),
                ]"
        ></v-text-field>
        <!-- <v-date-picker
          :label="$t('patient.creationForm.formFieldTitles.dateOfBirth')"
          v-model="patient.dateOfBirth"
          outlined
          required
          :disabled="patient.id > 0"
                ></v-date-picker>-->
        <v-menu
          dense
          hide-details=""
          class="pb-2"
          ref="menu"
          v-model="menuDoB"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              dense
              hide-details=""
              class="pb-2"
              append-icon
              v-model="dateOfBirth"
              :label="
                getTranslation(
                  'patient.creationForm.formFieldTitles.dateOfBirth',
                  '*'
                )
              "
              :rules="[v =>  (v || '' ).length > 0 || getTranslation('patient.creationForm.errors.dateOfBirthRequired')]"
              readonly
              outlined
              v-on="on"
              v-bind="attrs"
            ></v-text-field>
          </template>
          <v-date-picker
            dense
            hide-details=""
            class="pb-2"
            ref="picker"
            show-adjacent-months
            v-model="dateOfBirth"
            @input="menuDoB = false"
            :max="new Date(Date.now()).toISOString().substr(0, 10)"
            :min="minDateOfBirth"
          ></v-date-picker>
        </v-menu>
        <v-select
          dense
          hide-details=""
          class="pb-2"
          :label="
            getTranslation(
              'patient.creationForm.formFieldTitles.diagnosis',
              '*'
            )
          "
          :items="diagnosisList"
          :loading="isDataLoading"
          required
          v-model="patient.diagnosisId"
          item-text="name"
          item-value="id"
          outlined
          :rules="[
            () =>
              !!patient.diagnosisId ||
              $t('patient.creationForm.errors.diagnosisRequired'),
          ]"
        ></v-select>
        <v-text-field
          dense
          hide-details=""
          class="pb-2"
          :label="
            getTranslation(
              'patient.creationForm.formFieldTitles.physician',
              '*'
            )
          "
          outlined
          required
          v-model="patient.physician"
          :rules="[v =>  (v || '' ).length > 0 || getTranslation('patient.creationForm.errors.physicianRequired')]"
        ></v-text-field>
        <v-checkbox
          dense
          hide-details=""
          class="pb-2 mt-1"
          :label="
            getTranslation(
              'patient.creationForm.formFieldTitles.inhibitors'
            )
          "
          v-model="patient.inhibitors"
        ></v-checkbox>
        <v-menu
          dense
          hide-details=""
          class="pb-2"
          ref="menu"
          v-model="menuInhibitorsDate"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :disabled="!patient.inhibitors"
              dense
              clearable
              @click:clear="onClearInhibitorsDate"
              hide-details=""
              class="pb-2"
              append-icon
              v-model="inhibitorsDate"
              :label="
                getTranslation(
                  'patient.creationForm.formFieldTitles.inhibitorsDate'
                )
              "
              :rules="[]"
              readonly
              outlined
              v-on="on"
              v-bind="attrs"
            ></v-text-field>
          </template>
          <v-date-picker
            dense
            hide-details=""
            class="pb-2"
            ref="pickerInhibitors"
            show-adjacent-months
            v-model="inhibitorsDate"
            @input="menuInhibitorsDate = false"
            :max="new Date(Date.now()).toISOString().substr(0, 10)"
            min="1900-01-01"
          ></v-date-picker>
        </v-menu>
        <v-switch
          :label="$t('patient.creationForm.formFieldTitles.isActive')"
          v-model="patient.isActive"
        ></v-switch>
      </v-card-text>
      <div class="d-flex">
        <v-spacer />
        <v-btn
          class="mr-4"
          @click="save()"
          color="success"
          :disabled="isSending || !isValid || isValidatingPatient || isValidatingWBRDId"
        >
          {{ $t("actions.save") }}
        </v-btn>
        <v-progress-circular
          v-if="isSending"
          indeterminate
        ></v-progress-circular>
      </div>
    </v-form>
  </v-card>
</template>
<script>
import axios from "axios";
export default {
  props: ["parameter"],
  data() {
    return {
      isValid: false,
      isSending: false,
      isShowContent: false,
      snackBarMessage: "SAVED",
      showSnackbarColor: "success",
      patient: { id: null, dateOfBirth: null, isActive: true, inhibitors: false, inhibitorsDate: null },
      htcList: [],
      filteredHTCList: [],
      nmoList: [],
      diagnosisList: [],
      menuDoB: false,
      menuInhibitorsDate: false,
      timeout: undefined,
      patientExists: false,
      isValidatingPatient: false,
      wbdrIdTimeout: undefined,
      wbdrIdExists: false,
      isValidatingWBRDId: false
    };
  },
  computed: {
    canUpdatePatientId() {
      if (
        !(
          this.patient.id > 0 ||
          this.htcList.length < 1 ||
          this.nmoList.length < 1
        )
      ) {
        return true;
      } else if (!this.isUserDoctor) {
        return true;
      } else {
        return false;
      }
    },
    isUserAdmin() {
      return this.$store.getters.getIsAdmin;
    },
    isUserNMOAdmin() {
      return this.$store.getters.getIsNMOAdmin;
    },
    isUserDoctor() {
      return !this.isUserAdmin && !this.isUserNMOAdmin;
    },
    // htcId: {
    //   get() {
    //     return this.patient.htcId;
    //   },
    //   set(val) {
    //     this.patient.htcId = val.id;
    //   },
    // },
    dateOfBirth: {
      get: function () {
        if (this.patient.dateOfBirth == null) {
          return null;
        }
        var d = this.$moment(this.patient.dateOfBirth).format("YYYY-MM-DD");

        return d;
      },
      set: function (val) {
        this.patient.dateOfBirth = this.$moment(val).format("YYYY-MM-DD");
      },
    },
    inhibitorsDate: {
      get: function () {
        if (this.patient.inhibitorsDate == null) {
          return null;
        }
        var d = this.$moment(this.patient.inhibitorsDate).format("YYYY-MM-DD");

        return d;
      },
      set: function (val) {
        if (val == null) {
          return null;
        }
        this.patient.inhibitorsDate = this.$moment(val).format("YYYY-MM-DD");
      },
    },
    nmoId() {
      return this.patient.nmoId;
    },
    patientId() {
      return this.patient.patientId;
    },
    minDateOfBirth() {
      let minDate = new Date(Date.now());
      minDate.setFullYear(minDate.getFullYear() - 100);
      
      return minDate.toISOString().substr(0, 10);
    },
  },
  methods: {
    onSetTitle(title) {
      this.$emit("onSetTitle", title);
    },
    async loadPatient() {
      this.isDataLoading = true;
      await axios
        .get(
          `${this.$store.getters.getEnvironment}patient/${this.parameter.id}`
        )
        .then((res) => {
          this.patient = res.data;
          this.isDataLoading = false;
        })
        .catch((e) => {});
    },
    async loadNMOs() {
      this.isDataLoading = this.$store.state.loadingBarColor;
      await axios
        .get(`${this.$store.getters.getEnvironment}nmo?nameOnly=true`)
        .then((res) => {
          this.nmoList = res.data; //.map((x) => ({ name: x.name, value: x.id }));
          this.isDataLoading = false;
        })
        .catch((e) => {});
    },
    async loadHTCs() {
      this.isDataLoading = this.$store.state.loadingBarColor;
      await axios
        .get(`${this.$store.getters.getEnvironment}htc`)
        .then((res) => {
          this.htcList = res.data; //.map((x) => ({ text: x.name, value: x.id }));
          this.isDataLoading = false;
        })
        .catch((e) => {});
    },
    async loadDiagnosis() {
      this.isDataLoading = this.$store.state.loadingBarColor;
      await axios
        .get(`${this.$store.getters.getEnvironment}Diagnosis`)
        .then((res) => {
          var diagnosis = res.data;
          diagnosis = diagnosis.map((x) => {
            x.name = this.$t(`patient.diagnosis.${x.name}`);
            return x;
          });
          this.diagnosisList = diagnosis;
          this.isDataLoading = false;
        })
        .catch((e) => {});
    },
    save() {
      if (this.patient.id == null) this.insert();
      else this.update();
    },
    insert() {
      this.isSending = true;
      axios
        .post(this.$store.getters.getEnvironment + "patient/", this.patient)
        .then((response) => {
          this.patient.id = response.data.data.id;
          this.isSending = false;
          this.$store.commit("setPatientKey");
          this.close();
        })
        .catch((e) => {this.isSending = false;});
    },
    update() {
      this.isSending = true;
      axios
        .put(
          this.$store.getters.getEnvironment + "patient/" + this.patient.id,
          this.patient
        )
        .then((response) => {
          this.patient.id = response.data.data.id;
          this.isSending = false;
          this.$store.commit("setPatientKey");
          this.close();
        })
        .catch((e) => {this.isSending = false;});
    },
    close() {
      this.$store.commit("hideModal");
    },
    getTranslation(translationPath, ...extra) {
      return `${this.$t(translationPath)}${extra.reduce(
        (acc, cur) => "" + acc + cur,
        ""
      )}`;
    },
    // saveDoB(date) {
    //   this.$refs.menuDoB.save(date);
    // },
    validatePatientUniqueness() {
      this.isValidatingPatient = true;
      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        var url = `${this.$store.getters.getEnvironment}patient/search?patientId=${this.patient.patientId}&nmoId=${this.patient.nmoId}`
        if (this.patient.id != null)
          url += `&id=${this.patient.id}`

        axios
          .get(
            url
          )
          .then((res) => {
            this.isValidatingPatient = false;
            this.patientExists = res.data.any;
            this.$refs["patientId"].validate();
          })
          .catch((e) => {
            this.isValidatingPatient = false;
            this.patientExists = 0;
          });
      }, 500);
    },
    validateWBDRIdUniqueness() {
      this.isValidatingWBRDId = true;
      if (this.wbdrIdTimeout) clearTimeout(this.wbdrIdTimeout);
      this.wbdrIdTimeout = setTimeout(() => {
        var url = `${this.$store.getters.getEnvironment}patient/searchWBDRId?wbdrId=${this.patient.wbdrId}`
        if (this.patient.id != null)
          url += `&id=${this.patient.id}`

        axios
          .get(
            url
          )
          .then((res) => {
            this.isValidatingWBRDId = false;
            this.wbdrIdExists = res.data.any;
            this.$refs["wbdrId"].validate();
          })
          .catch((e) => {
            this.isValidatingWBRDId = false;
            this.wbdrIdExists = 0;
          });
      }, 500);
    },
    autoFillLocationIdentifiers() {
      const htcId = this.$store.getters.getHTCId;
      const nmoId = this.$store.getters.getNMOId;

      this.patient = { ...this.patient, htcId, nmoId };
      
    },
    onClearInhibitorsDate() {
      this.patient.inhibitorsDate = null;
    },
    onKeydownWBDRId(e) {
      var arrowsKeyCodes = [37, 38, 39, 40];
      var numPadNumberKeyCodes = [96, 97, 98, 99, 100, 101, 102, 103, 104, 105];
      var tabBackDel = [8, 9, 46];

      this.validateWBDRIdUniqueness();

      if (this.patient.wbdrId?.length > 4 && arrowsKeyCodes.indexOf(e.keyCode) === -1 && tabBackDel.indexOf(e.keyCode) === -1)
        e.preventDefault();

      // allow only [0-9] number, numpad number, arrow,  BackSpace, Tab, Del
      if (
          (e.keyCode < 48 &&
              arrowsKeyCodes.indexOf(e.keyCode) === -1 || e.keyCode > 57 &&
              numPadNumberKeyCodes.indexOf(e.keyCode) === -1
          ) &&
          tabBackDel.indexOf(e.keyCode) === -1
      ) {
          e.preventDefault();
      }
    }
  },
  async created() {
    this.onSetTitle(this.$t("patient.view.edit"));

    await this.loadNMOs();
    await this.loadHTCs();
    await this.loadDiagnosis();
    if (this.parameter.id != -1) {
      this.loadPatient();
    } else {
      this.autoFillLocationIdentifiers();
    }
    this.$refs.form.resetValidation();
  },
  watch: {
    menuDoB(val) {
      val && setTimeout(() => (this.$refs.picker.activePicker = "YEAR"));
    },
    menuInhibitorsDate(val) {
      val && setTimeout(() => (this.$refs.pickerInhibitors.activePicker = "YEAR"));
    },
    nmoId(val) {
      this.filteredHTCList = this.htcList.filter((x) => {
        return x.nmoId == val;
      });
    },
  },
};
</script>